/**
 * TIMELINE LOGIC: Pure functions for temporal-to-spatial transformations.
 * Centralized for consistency across Timeline and Scheduler views.
 */

import { toLogicDate } from '@/utils/date-utils';

/**
 * Calculates the visual Y position for a given date on the timeline.
 */
export const getPosition = (dateStr: string | Date, selectedDate: Date, hourHeight: number): number => {
    const date = new Date(dateStr);
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);

    let targetDate = date;
    if (date < dayStart) targetDate = dayStart;
    if (date > dayEnd) targetDate = dayEnd;

    const minutes = (targetDate.getHours() * 60) + targetDate.getMinutes();
    return minutes * (hourHeight / 60);
};

/**
 * Calculates the Date object for a given Y coordinate.
 */
export const getTimeFromY = (y: number, selectedDate: Date, hourHeight: number): Date => {
    const totalMinutes = y / (hourHeight / 60);
    const hour = Math.floor(totalMinutes / 60);
    const minute = Math.floor(totalMinutes % 60);

    const date = new Date(selectedDate);
    date.setHours(hour, minute, 0, 0);
    return date;
};

/**
 * Determines the time granularity based on the current zoom level (hourHeight).
 */
export const getGranularity = (hourHeight: number): number => {
    if (hourHeight < 50) return 60; // 1 hour
    if (hourHeight < 120) return 30; // 30 minutes
    return 15; // 15 minutes
};

/**
 * Snaps a date to the nearest interval.
 */
export const snapTime = (date: Date, intervalMinutes: number): Date => {
    const minutes = date.getHours() * 60 + date.getMinutes();
    const snappedMinutes = Math.floor(minutes / intervalMinutes) * intervalMinutes;
    
    const snappedDate = new Date(date);
    snappedDate.setHours(Math.floor(snappedMinutes / 60), snappedMinutes % 60, 0, 0);
    return snappedDate;
};

/**
 * Calculates positioned activity boxes with clustering for overlapping events.
 * HARDENED: Standardized on toLogicDate for reliable cross-timezone simulation.
 * FIXED: Mismatch where short/completed events without end times were hidden.
 */
export const getPositionedActivities = (events: any[], selectedDate: Date, hourHeight: number) => {
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);

    const rawBoxes = events.map(act => {
        const isPlannedType = act.status === 'PLANNED' || act.status === 'SKIPPED';
        const rawStart = isPlannedType ? (act.start_time || `${toLogicDate(selectedDate)}T06:00:00`) : act.start_time;
        
        // HARDENED: Ensure even completed events without end_time have a visual presence (min 15 mins)
        const defaultDuration = 15 * 60000;
        const rawEnd = act.end_time || new Date(new Date(rawStart).getTime() + (isPlannedType ? 3600000 : defaultDuration)).toISOString();
        
        const eventStart = new Date(rawStart);
        const eventEnd = new Date(rawEnd);

        // --- SPANNING LOGIC: CLIP TO SELECTED DAY ---
        const effectiveStart = eventStart < dayStart ? dayStart : eventStart;
        const effectiveEnd = eventEnd > dayEnd ? dayEnd : eventEnd;

        // Skip if event doesn't actually overlap with this day
        if (effectiveEnd <= effectiveStart && eventStart.getTime() !== eventEnd.getTime()) return null;
        
        // If start/end same, force min height
        const top = getPosition(effectiveStart, selectedDate, hourHeight);
        let height = getPosition(effectiveEnd, selectedDate, hourHeight) - top;
        if (height <= 0) height = hourHeight / 4; // Min 15 mins visual height
        
        return { ...act, top, height, bottom: top + height, column: 0, totalCols: 1 };
    }).filter(Boolean).sort((a, b) => a!.top - b!.top);

    const clusters: any[][] = [];
    rawBoxes.forEach(box => {
        let added = false;
        for (let cluster of clusters) {
            if (cluster.some(c => box.top < c.bottom && box.bottom > c.top)) {
                cluster.push(box);
                added = true;
                break;
            }
        }
        if (!added) clusters.push([box]);
    });

    const positionedActivities: any[] = [];
    clusters.forEach(cluster => {
        const columns: any[][] = [];
        cluster.forEach(box => {
            let colIndex = 0;
            while (true) {
                if (!columns[colIndex]) { columns[colIndex] = [box]; box.column = colIndex; break; }
                const lastInCol = columns[colIndex][columns[colIndex].length - 1];
                if (box.top >= lastInCol.bottom) { columns[colIndex].push(box); box.column = colIndex; break; }
                colIndex++;
            }
        });
        cluster.forEach(box => { box.totalCols = columns.length; positionedActivities.push(box); });
    });

    return positionedActivities;
};
